import Event from 'base/event'
import Hub from 'base/hub'
import store from 'base/store'
import addScript from 'base/lib/addScript'
import getQueryParam from 'base/lib/get_query_param'
import { sha256 as Sha256 } from 'sha.js'

function hashPII(value) {
  const sha = new Sha256()
  return sha.update(value).digest('hex')
}

function hashUUID(data = []) {
  if (data.length === 0) {
    return ''
  }

  const sha = new Sha256()
  return sha.update(data.join('+')).digest('hex')
}

function getUserDetails() {
  const user = store.getState().session.user
    ? store.getState().session.user
    : store.getState().session.visitor

  return {
    em: user.email ? hashPII(user.email) : null,
    fn: user.first_name ? hashPII(user.first_name.toLowerCase()) : null,
    ln: user.last_name ? hashPII(user.last_name.toLowerCase()) : null,
  }
}

function getSessionId() {
  return store.getState().session.visitor.session_id
}

function loadFacebook() {
  if (!store.getState().appConfig.isProductionLike) {
    return
  }

  addScript('//connect.facebook.net/en_US/fbevents.js', { async: true })
}

function setupFbq() {
  // FB pixel code from
  // https://developers.facebook.com/docs/facebook-pixel/using-the-pixel
  //
  // The last two lines of the original code were moved to loadFB() to be
  // called separately:
  //
  // s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)
  //
  // This is done so window.fbq can be initialized immediately on page load.

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = !0
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = !0
    t.src = v
  })(window, document, 'script', '//connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */
  window.fbq('init', '1658356961070972')
  window.fbq('track', 'PageView')
}

export default function setupFacebook() {
  if (!store.getState().appConfig.isProductionLike) {
    return
  }

  // Somewhat unreleated signal for paid marketing
  if (getQueryParam('utm_source') === 'facebook') {
    Hub.pub(Event.PaidMarketing.FACEBOOK_LAND)
  }

  setupFbq()

  Hub.sub(
    Event.App.ROUTE,
    () => {
      window.fbq('track', 'PageView')
    },
    { late: true },
  )

  Hub.sub(
    Event.Checkout.ADD_TO_CART,
    (event, data) => {
      const eventID = hashUUID([
        'AddToCart',
        getSessionId(),
        data.variant.id,
        parseInt(data.timestamp / 1000, 10),
      ])
      // Note that data.lineItem is a variant object, per /base/actions/asynchronous.js
      window.fbq(
        'track',
        'AddToCart',
        {
          event_id: eventID,
          content_type: 'product',
          content_ids: [`${data.variant.product_id}-${data.variant.id}`],
          content_name: data.variant.display_name,
          value: data.variant.price,
          currency: 'USD',
          ...getUserDetails(),
        },
        { eventID },
      )
    },
    { late: true },
  )

  Hub.sub(
    Event.Checkout.COMPLETE,
    (event, order) => {
      const productIds = order.line_items.map(
        lineItem => `${lineItem.product_id}-${lineItem.variant_id}`,
      )
      const productNames = order.line_items.map(lineItem => lineItem.display_name)
      const eventID = hashUUID(['Purchase', order.number])

      window.fbq(
        'track',
        'Purchase',
        {
          event_id: eventID,
          content_type: 'product',
          content_ids: productIds,
          content_name: productNames,
          value: order.total_in_usd,
          currency: 'USD',
          new_activation: order.is_first_order,
          ...getUserDetails(),
        },
        { eventID },
      )
    },
    { late: true },
  )

  Hub.sub(
    Event.Product.PAGE_VIEW,
    (event, data) => {
      window.fbq('track', 'ViewContent', {
        content_type: 'product_group',
        content_ids: [data.product.id],
        content_name: data.product.display_name,
        content_category: data.product.id,
        value: data.product.price,
        currency: 'USD',
      })
    },
    { late: true },
  )

  Hub.sub(
    Event.LandingPage.WAITLIST_SUCCESSFUL,
    (event, page) => {
      switch (page.type) {
        case 'DenimDrops':
          window.fbq('track', 'DenimDropWaitlist')
          break
        case 'Rainboot':
          window.fbq('track', 'RainbootWaitlist')
          break
        default:
      }
    },
    { late: true },
  )

  Hub.sub(
    Event.User.SIGN_UP,
    (event, data) => {
      const eventID = hashUUID(['SignUp', getSessionId(), parseInt(data.timestamp / 1000, 10)])
      window.fbq(
        'track',
        'SignUp',
        {
          event_id: eventID,
          ...getUserDetails(),
        },
        { eventID },
      )
    },
    { late: true },
  )

  Hub.sub(
    Event.User.SIGN_IN,
    (event, data) => {
      const eventID = hashUUID(['SignIn', getSessionId(), parseInt(data.timestamp / 1000, 10)])
      window.fbq(
        'track',
        'SignIn',
        {
          event_id: eventID,
          ...getUserDetails(),
        },
        { eventID },
      )
    },
    { late: true },
  )
}

export { loadFacebook }
